<template>
  <div class="h-full">
    <!-- v2 Console preview announcement. Shown on every authenticated page
         (incl. the org list), hidden on SignUp/login. The target is derived from
         the current host (v2.<host>), so it points at the right env automatically. -->
    <ds-banner
      v-if="showV2Banner && !loading"
      id="v2-console-preview-banner"
      :is-closeable="true"
      tone="warning"
      class="md:fixed"
      @close="dismissV2Banner"
    >
      <span class="text-lg">
        A new version of the StreamNative Cloud Console (v2) is now available to preview.
        <V2ConsoleLink :href="v2ConsoleUrl" variant="banner" :target="v2ConsoleTarget" />
      </span>
    </ds-banner>
    <div v-if="route.name !== 'OrganizationsPage' && route.name !== 'SignUp'">
      <ds-banner v-if="isSubscriptionEndBannerDisplay" :is-closeable="false" class="md:fixed">
        <span class="text-lg">
          The expiration date of your StreamNative subscription is {{ subscriptionEndDate }}. To
          continue using StreamNative Cloud without interruptions, please renew your subscription by
          contacting
          <a href="mailto:sales@streamnative.io" target="_blank"> sales@streamnative.io </a>
          .
        </span>
      </ds-banner>
      <ds-banner v-if="isDiscountEndBannerDisplay" :is-closeable="false" class="md:fixed">
        <span class="text-lg">
          Applied discount is exhausted, to ensure uninterrupted service please configure
          <a id="billing-portal-link" target="_blank">payment method</a> or contact
          <a href="mailto:sales@streamnative.io" target="_blank"> sales@streamnative.io.</a>
        </span>
      </ds-banner>
      <ds-banner
        v-if="
          activeOrganization?.metadata?.name &&
          appliedDiscountValueCent &&
          !isDiscountEndBannerDisplay
        "
        :id="`intro-discount-banner-${activeOrganization?.metadata?.name}`"
        :is-closeable="true"
        class="md:fixed"
        @close="onBannerClose"
      >
        <span class="text-lg">
          Welcome to StreamNative! You are starting
          <b>${{ Math.floor(appliedDiscountValueCent / 100) }}</b> free trial credit, which is
          applied to <b>{{ activeOrganization?.metadata?.name }}</b> org,
          <b>{{ discountCreditRemainingPretty }}</b> credit is remaining.
        </span>
      </ds-banner>

      <NbModal ref="billingAndPayment" title="Billing & Payment" size="m" footer-hidden>
        <template #trigger="">
          <div />
        </template>
        <template #default="">
          <div v-if="sugerPartner">
            Service is subscribed via the {{ sugerPartner.toUpperCase() }} marketplace and your
            billing and payment details are managed directly under your
            {{ sugerPartner.toUpperCase() }} cloud account.
            <br />
            <br />
            Please see your
            {{ sugerPartner.toUpperCase() }} billing/payment for more details.
          </div>
          <div v-else-if="stripePortalUrl">
            <a :href="stripePortalUrl" target="_blank" class="text-primary hover:underline">
              Stripe Portal
            </a>
          </div>
        </template>
      </NbModal>
    </div>
    <metainfo>
      <template #title="{ content }">{{ content ? `${content}` : `StreamNative Cloud` }}</template>
    </metainfo>
    <router-view v-if="!loading" />
    <div v-if="loading" class="absolute indent-1 h-full w-full flex justify-center items-center">
      <DsLoading class="w-12" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useMeta } from 'vue-meta'
import { useAnalytics } from '@/composables/useAnalytics'
import { usePylon } from '@/composables/usePylon'
import { useV2Banner } from '@/composables/useV2Banner'
import * as Sentry from '@sentry/vue'
import type { PulsarState } from './composables/usePulsarState'
import { until } from '@vueuse/core'
import { auth } from './auth'
import '/src/stories/scss/nb-base.scss'
import { onBeforeMount, onMounted } from 'vue'
import { withRetry } from './utils/asyncHelper'

const billingAndPayment = ref<any | undefined>(undefined)
const stripePortalUrl = ref<string>('')

const { locale } = useI18n()
const router = useRouter()
const route = useRoute()
const loading = ref<boolean>(true)

// v2 Console preview banner (shared with MainLayout, which reserves the top
// space so the sticky banner doesn't overlap content). See useV2Banner.
const { v2ConsoleUrl, v2ConsoleTarget, showV2Banner, dismissV2Banner } = useV2Banner()
useMeta({
  title: 'StreamNative Cloud',
  htmlAttrs: { lang: locale.value, amp: true }
})

const { user, isLoading } = auth
const { identifyUser } = useAnalytics()
const { initPylon } = usePylon()
const { isSubscriptionEndBannerDisplay, subscriptionEndDate, discountCreditRemainingPretty } =
  useSubscription()

const {
  activeOrganization,
  appliedDiscountValueCent,
  isDiscountEndBannerDisplay,
  openBillingAndPayment,
  isDiscountBannerClosed,
  sugerPartner,
  getStripePortalUrl
} = useOrganization()

const onBannerClose = () => {
  isDiscountBannerClosed.value = 'close'
}

onBeforeMount(async () => {
  // DO NOT REMOVE THESE AWAITS
  await router.isReady()
  await until(() => !isLoading.value).toBe(true)

  try {
    const urlSearchParams = new URLSearchParams(window.location.search)
    const params = Object.fromEntries(urlSearchParams.entries())
    const pulsarState: PulsarState = {
      organization: params.org ? params.org : undefined,
      instance: params.instance ? params.instance : undefined,
      clusterUid: params.cluster ? params.cluster : undefined,
      workspaceUid: params.workspace ? params.workspace : undefined,
      tenant: params.tenant ? params.tenant : undefined,
      namespace: params.namespace ? params.namespace : undefined,
      topic: params.topic ? params.topic : undefined
    }
    // below data syncs are needed for some pages initial load,
    // for example, we need to know if is the Function feature enabled
    // from the active cluster info before we can load FunctionsPage
    // so we need to wait for them

    // 1. initialize organization first to get the isInstanceEnabled value
    // if failed, retry 3 times with 1s, 2s, 3s delay by default
    await withRetry(async () => await useOrganization().init(pulsarState))

    // 2. initialize feature gates (depends on organization)
    await useFeature().init(pulsarState)

    // 3. depend on organization, to get the isInstanceEnabled value
    // 2. initialize instance first to get the instances value, which is needed for cluster map
    // if failed, retry 3 times with 1s, 2s, 3s delay by default
    await withRetry(async () => await useInstance().init(pulsarState))

    // 3. then initialize other data asyncs after organization and instance are initialized
    await Promise.all([
      useCluster().init(pulsarState),
      useKafkaCluster().init(pulsarState),
      useWorkspace().init(pulsarState),
      useTenantNamespace().init(pulsarState),
      useTopic().init(pulsarState)
    ]).catch(e => {
      console.error(e)
    })

    // below data syncs are not needed for the page initial load,
    // so we don't need to wait for them
    Promise.all([
      useEnvironment().init(pulsarState),
      useSubscription().init(pulsarState),
      useServiceAccount().init(pulsarState),
      useApiKey().init(pulsarState),
      useSecret().init(pulsarState),
      useUser().init(pulsarState)
      // eslint-disable-next-line promise/prefer-await-to-then
    ]).catch(e => {
      console.error(e)
    })

    if (user?.value?.email) {
      Sentry.setUser({
        email: user.value.email,
        id: user.value.sub,
        name: user.value.name
      })
    }

    identifyUser()
    initPylon()
  } catch (e) {
    useError(e)
  } finally {
    loading.value = false
  }
})

onMounted(() => {
  const billingPortalLink = document.getElementById('billing-portal-link')
  if (billingPortalLink) {
    billingPortalLink.onclick = () => {
      openBillingAndPayment()
    }
  }
  window.addEventListener('openBillingAndPayment', async () => {
    if (!activeOrganization.value?.spec?.suger && activeOrganization.value?.metadata?.name) {
      stripePortalUrl.value = await getStripePortalUrl(activeOrganization.value.metadata.name)
    }

    billingAndPayment.value?.openModal()
  })
})
</script>
